'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  companyName: string;
  foremanName: string;
  phone: string;
  email: string;
  crewCount: string;  // string for input handling, parsed at submit
  crewSize: string;
}

const INITIAL_FORM: FormData = {
  companyName: '',
  foremanName: '',
  phone: '',
  email: '',
  crewCount: '1',
  crewSize: '4-6 men',
};

const CREW_SIZE_OPTIONS = ['2-3 men', '4-6 men', '5-7 men', '6-8 men', '8+ men'] as const;

type FieldErrors = Partial<Record<keyof FormData, string>>;

// US phone validation: at least 10 digits (after stripping common formatting chars).
function isValidUsPhone(p: string): boolean {
  const digits = p.replace(/[\s().\-+]/g, '');
  return digits.length >= 10 && /^\d+$/.test(digits);
}

function isValidEmail(e: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function validate(data: FormData): FieldErrors {
  const errors: FieldErrors = {};
  if (!data.companyName.trim()) errors.companyName = 'Company name is required';
  if (!data.foremanName.trim()) errors.foremanName = 'Foreman name is required';
  if (!data.phone.trim())       errors.phone = 'Phone is required';
  else if (!isValidUsPhone(data.phone)) errors.phone = 'Enter a valid US phone (≥10 digits)';
  if (!data.email.trim())       errors.email = 'Email is required';
  else if (!isValidEmail(data.email))   errors.email = 'Enter a valid email address';
  const cc = parseInt(data.crewCount, 10);
  if (!Number.isFinite(cc) || cc < 1 || cc > 99) errors.crewCount = 'Crew count must be 1–99';
  if (!CREW_SIZE_OPTIONS.includes(data.crewSize as typeof CREW_SIZE_OPTIONS[number])) {
    errors.crewSize = 'Pick a crew size';
  }
  return errors;
}

const LOG = '[InstallerNetwork]';

export default function AddNewCrewDrawer({ open, onClose }: Props) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset form whenever the drawer reopens.
  useEffect(() => {
    if (open) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setShowSuccess(false);
    }
  }, [open]);

  const update = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = () => {
    const found = validate(formData);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    console.log(`${LOG} Add New Crew submit (demo, not persisted):`, formData);
    setShowSuccess(true);
    // Auto-dismiss banner + close drawer after 2s.
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 2000);
  };

  if (!open) return null;

  return (
    // Drawer matches the fixed top-[60px] / z-30 pattern of the crew detail
    // drawer so the app's top bar (z-header) stays sovereign.
    <div className="fixed top-[60px] left-0 right-0 bottom-0 z-30 flex justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-[520px] h-full bg-white shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Add New Crew</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
              suppressHydrationWarning
            >
              <Icon name="XMarkIcon" size={18} />
            </button>
          </div>
        </div>

        {/* Demo-mode banner — visible from the moment the drawer opens */}
        <div
          className="flex-shrink-0 px-6 py-2.5 border-b border-amber-200"
          style={{ background: '#FEF3C7', color: '#92400E' }}
        >
          <p className="text-[11px] font-semibold flex items-center gap-1.5">
            <Icon name="ExclamationTriangleIcon" size={13} />
            DEMO MODE — submissions are not saved. Real persistence is dev-team backlog work.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Success banner (inline; auto-dismiss after 2s) */}
          {showSuccess && (
            <div
              className="rounded-lg px-3 py-2.5 flex items-center gap-2"
              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)' }}
            >
              <Icon name="CheckCircleIcon" size={16} style={{ color: '#10B981' }} />
              <p className="text-xs font-semibold" style={{ color: '#047857' }}>
                Crew saved (demo) — closing drawer…
              </p>
            </div>
          )}

          <FormField label="Company Name" required error={errors.companyName}>
            <input
              type="text"
              value={formData.companyName}
              onChange={update('companyName')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              placeholder="e.g., Apex Roofing LLC"
            />
          </FormField>

          <FormField label="Foreman Name" required error={errors.foremanName}>
            <input
              type="text"
              value={formData.foremanName}
              onChange={update('foremanName')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              placeholder="e.g., Carlos Mendez"
            />
          </FormField>

          <FormField label="Phone" required error={errors.phone}>
            <input
              type="tel"
              value={formData.phone}
              onChange={update('phone')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              placeholder="(937) 555-0142"
            />
          </FormField>

          <FormField label="Email" required error={errors.email}>
            <input
              type="email"
              value={formData.email}
              onChange={update('email')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              placeholder="foreman@company.com"
            />
          </FormField>

          <FormField label="Crew Count" required error={errors.crewCount}>
            <input
              type="number"
              min={1}
              max={99}
              value={formData.crewCount}
              onChange={update('crewCount')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
            />
          </FormField>

          <FormField label="Crew Size" required error={errors.crewSize}>
            <select
              value={formData.crewSize}
              onChange={update('crewSize')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
            >
              {CREW_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </FormField>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-all"
            suppressHydrationWarning
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={showSuccess}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: '#07B0E9' }}
            suppressHydrationWarning
          >
            Save Crew
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
      {error && (
        <p className="text-[11px] mt-1" style={{ color: '#B91C1C' }}>{error}</p>
      )}
    </div>
  );
}
